import React, { useContext } from "react";
import { Popover } from "../../../../../design_system/components/ToolTip/ToolTip.jsx";
import { PopupContext } from "../../../../../design_system/components/Popup/PopupContext";
import { extractTreeDetails, getValueLabel, getBooleanValueLabel, normalizeValue } from "../utils/utils";
import resolveChildren from "./resolveChildren";

const formatRuleValues = (rule, config) => {
  const booleanLabel = getBooleanValueLabel(rule.field, rule.operator, config);
  if (booleanLabel) return [booleanLabel];

  const values = normalizeValue(rule?.value?.value);
  if (!values.length || rule.operatorLabel === "is empty") {
    return [];
  }

  const label = getValueLabel(rule.field, values.length === 1 ? values[0] : values, config);
  return normalizeValue(label);
};

const RuleChip = ({ rule, config }) => {
  const values = formatRuleValues(rule, config);
  const popupCtx = useContext(PopupContext);

  if (!values.length) {
    return (
      <div className="custom-report-builder__applied-filter-chip">
        <span className="custom-report-builder__applied-filter-chip-part is-field">{rule.fieldLabel}</span>
        <span className="custom-report-builder__applied-filter-chip-part is-operator">{rule.operatorLabel}</span>
      </div>
    );
  }

  const firstValue = values[0];
  const otherValues = values.slice(1);

  return (
    <div className="custom-report-builder__applied-filter-chip">
      <span className="custom-report-builder__applied-filter-chip-part is-field">{rule.fieldLabel}</span>
      <span className="custom-report-builder__applied-filter-chip-part is-operator">{rule.operatorLabel}</span>
      <span className="custom-report-builder__applied-filter-chip-part is-value">{firstValue}</span>
      {otherValues.length > 0 && (
        <Popover
          tooltipText={() => (
            <div style={{ maxHeight: 100, overflowY: 'auto', padding: 'var(--space-s) var(--space-m)' }}>
              {otherValues.map((val, idx) => (<div key={idx}>{val}</div>))}
            </div>
          )}
          placement="bottom"
          theme="dark"
          customStyle={{ ...(popupCtx?.zIndex ? { zIndex: popupCtx.zIndex + 10 } : {}), padding: 0 }}
        >
          <span className="custom-report-builder__applied-filter-chip-part is-more">+{otherValues.length} more</span>
        </Popover>
      )}
    </div>
  );
};

const Divider = ({ label }) => (
  <div className="custom-report-builder__applied-filter-divider">
    <span className="custom-report-builder__applied-filter-divider-line" />
    <span className="custom-report-builder__applied-filter-divider-label">{label}</span>
    <span className="custom-report-builder__applied-filter-divider-line" />
  </div>
);

const GroupCard = ({ groupId, groups, rules, config }) => {
  const group = groups[groupId];
  const children = resolveChildren(groupId, groups, rules);

  return (
    <div className="custom-report-builder__applied-filter-group-card">
      {children.map((child, index) => (
        <React.Fragment key={child.id}>
          {index > 0 && (
            <Divider label={group?.conjunction === "OR" ? "Or" : "And"} />
          )}
          {child.type === "rule" ? (
            <RuleChip rule={child.data} config={config} />
          ) : (
            <GroupCard groupId={child.id} groups={groups} rules={rules} config={config} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export const AppliedFiltersPopupPanel = ({ tree, config, subtitle = "" }) => {
  const { rootGroupId, groups, rules } = extractTreeDetails(tree, config);
  const rootChildren = rootGroupId ? resolveChildren(rootGroupId, groups, rules) : [];
  const rootConjunction = groups[rootGroupId]?.conjunction || "AND";

  return (
    <div className="custom-report-builder__applied-filters-popup-panel">
      {subtitle && (
        <div className="custom-report-builder__applied-filters-popup-subtitle">{subtitle}</div>
      )}
      <div className="custom-report-builder__applied-filters-popup-list">
        {rootChildren.map((child, index) => (
          <React.Fragment key={child.id}>
            <div className="custom-report-builder__applied-filters-popup-item">
              <div className="custom-report-builder__applied-filters-popup-item-label">
                {`Filter ${index + 1}${child.type === "group" ? " - group" : ""}`}
              </div>
              {child.type === "rule" ? (
                <RuleChip rule={child.data} config={config} />
              ) : (
                <GroupCard groupId={child.id} groups={groups} rules={rules} config={config} />
              )}
            </div>
            {index < rootChildren.length - 1 && (
              <Divider label={rootConjunction === "OR" ? "Or" : "And"} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default AppliedFiltersPopupPanel;
